/**
 * AnalysisResults Component
 *
 * Enhanced with Bob's UX recommendations:
 * - R6: Structured Gemini output (scores + timestamped findings)
 * - R7: Clickable timestamp findings that jump video to that point
 * - R8: WHOOP-style composite score dials
 *
 * Progressive disclosure: Scores -> Findings -> Full Text
 * Mode: Deep Analysis (Mode 3)
 */
import React, { useState } from 'react';
import { Loader2, AlertTriangle, AlertCircle, Info, CheckCircle2 } from 'lucide-react';

// ============================================================================
// Score Dial (WHOOP-style) — R8
// ============================================================================

function ScoreDial({ label, value, max = 10 }) {
  const pct = Math.min((value / max) * 100, 100);
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  // Color coding: green (7-10), amber (4-6.9), red (0-3.9)
  let color = '#22c55e'; // green
  let bgColor = 'rgba(34,197,94,0.15)';
  if (value < 4) {
    color = '#ef4444'; // red
    bgColor = 'rgba(239,68,68,0.15)';
  } else if (value < 7) {
    color = '#f59e0b'; // amber
    bgColor = 'rgba(245,158,11,0.15)';
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-24 h-24" style={{ backgroundColor: bgColor, borderRadius: '50%' }}>
        <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
          <circle
            cx="40" cy="40" r={radius} fill="none"
            stroke={color} strokeWidth="6" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-bold text-zinc-100" style={{ color }}>
            {typeof value === 'number' ? value.toFixed(1) : value}
          </span>
        </div>
      </div>
      <span className="text-xs text-zinc-400 text-center">{label}</span>
    </div>
  );
}

// ============================================================================
// Risk Badge
// ============================================================================

function RiskBadge({ level }) {
  const config = {
    low: { color: '#22c55e', bg: 'rgba(34,197,94,0.15)', label: 'Low Risk' },
    medium: { color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', label: 'Medium Risk' },
    high: { color: '#ef4444', bg: 'rgba(239,68,68,0.15)', label: 'High Risk' },
  };
  const c = config[level] || config.medium;

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="w-24 h-24 rounded-full flex items-center justify-center"
        style={{ backgroundColor: c.bg }}
      >
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center border-4"
          style={{ borderColor: c.color }}
        >
          {level === 'low' && <CheckCircle2 size={28} style={{ color: c.color }} />}
          {level === 'medium' && <AlertTriangle size={28} style={{ color: c.color }} />}
          {level === 'high' && <AlertCircle size={28} style={{ color: c.color }} />}
        </div>
      </div>
      <span className="text-xs text-zinc-400 text-center">{c.label}</span>
    </div>
  );
}

// ============================================================================
// Finding Card — R7
// ============================================================================

function FindingCard({ finding, onClickTimestamp }) {
  const severityConfig = {
    error: { icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
    warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
    info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  };
  const cfg = severityConfig[finding.severity] || severityConfig.info;
  const Icon = cfg.icon;

  return (
    <div className={`${cfg.bg} ${cfg.border} border rounded-lg p-3`}>
      <div className="flex items-start gap-3">
        <Icon size={16} className={`${cfg.color} mt-0.5 flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => onClickTimestamp && onClickTimestamp(finding.timestamp)}
              className="text-xs font-mono text-orange-400 hover:text-orange-300 bg-orange-400/10 px-1.5 py-0.5 rounded cursor-pointer"
            >
              {finding.timestamp}
            </button>
            <span className="text-xs text-zinc-500 rounded-full px-2 py-0.5 bg-white/5">
              {finding.category}
            </span>
          </div>
          <p className="text-sm text-zinc-200">{finding.description}</p>
          {finding.recommendation && (
            <p className="text-xs text-zinc-400 mt-1 italic">{finding.recommendation}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export const AnalysisResults = ({ isLoading, analysis, analysisResult, onSeekToTimestamp }) => {
  const [showFullText, setShowFullText] = useState(false);
  const scores = analysisResult?.scores;
  const findings = analysisResult?.findings || [];
  const detailedAnalysis = analysisResult?.detailedAnalysis || analysis;

  return (
    <div className="bg-[#0b1118] border border-white/10 rounded-xl p-5 min-h-[350px]">
      <h3 className="text-lg font-medium text-zinc-100 mb-4">Analysis Results</h3>

      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center h-[300px] gap-3">
          <Loader2 className="h-8 w-8 text-orange-400 animate-spin" />
          <p className="text-zinc-400">Analyzing your technique...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !analysis && (
        <div className="flex flex-col items-center justify-center h-[300px] text-center text-zinc-500">
          <Info size={48} className="mb-4 text-orange-400/50" />
          <p>Upload a video and ask a question to receive expert feedback</p>
        </div>
      )}

      {/* Results with Progressive Disclosure */}
      {!isLoading && analysis && (
        <div className="space-y-6">
          {/* Level 0: Composite Score Dials (R8) */}
          {scores && (
            <div className="grid grid-cols-3 gap-4 pb-4 border-b border-white/10">
              <ScoreDial label="Form Quality" value={scores.formQuality} max={10} />
              <ScoreDial label="Efficiency" value={scores.movementEfficiency} max={10} />
              <RiskBadge level={scores.riskLevel} />
            </div>
          )}

          {/* Level 1: Timestamped Findings (R7) */}
          {findings.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-zinc-300 mb-2">
                Findings ({findings.length})
              </h4>
              {findings.map((f, i) => (
                <FindingCard
                  key={i}
                  finding={f}
                  onClickTimestamp={onSeekToTimestamp}
                />
              ))}
            </div>
          )}

          {/* Level 2: Full Detailed Analysis (collapsible) */}
          {detailedAnalysis && (
            <div>
              <button
                onClick={() => setShowFullText(!showFullText)}
                className="text-sm text-zinc-400 hover:text-zinc-200 cursor-pointer flex items-center gap-1"
              >
                {showFullText ? 'Hide' : 'View'} Full Analysis
                <span className="text-xs">{showFullText ? '\u25B2' : '\u25BC'}</span>
              </button>
              {showFullText && (
                <div className="mt-3 text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed max-h-[400px] overflow-y-auto pr-2">
                  {detailedAnalysis}
                </div>
              )}
            </div>
          )}

          {/* Fallback: if no structured data, show raw analysis */}
          {!scores && !findings.length && analysis && (
            <div className="overflow-y-auto max-h-[400px] pr-2 text-zinc-300">
              {analysis.split('\n').map((paragraph, i) => {
                if (paragraph.startsWith('##')) {
                  return <h3 key={i} className="text-orange-400 font-medium mt-4">{paragraph.replace('##', '').trim()}</h3>;
                } else if (paragraph.startsWith('\u2022') || paragraph.startsWith('-')) {
                  return <li key={i} className="ml-4 text-sm">{paragraph.substring(1).trim()}</li>;
                } else if (paragraph.trim() === '') {
                  return <br key={i} />;
                } else {
                  return <p key={i} className="text-sm mb-2">{paragraph}</p>;
                }
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
